import { initializeApp, FirebaseApp } from 'firebase/app';
import { getMessaging, getToken, onMessage, Messaging } from 'firebase/messaging';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY as string | undefined,
  authDomain: `${import.meta.env.VITE_FIREBASE_PROJECT_ID}.firebaseapp.com`,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID as string | undefined,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID as string | undefined,
  appId: import.meta.env.VITE_FIREBASE_APP_ID as string | undefined,
};

const VAPID_KEY = import.meta.env.VITE_FIREBASE_VAPID_KEY as string | undefined;

/** True only when every field the web SDK actually needs is present — same convention as fcm.service.ts's isConfigured() on the backend. */
export function isPushConfigured(): boolean {
  return !!(firebaseConfig.apiKey && firebaseConfig.projectId && firebaseConfig.appId && VAPID_KEY);
}

let app: FirebaseApp | null = null;
let messaging: Messaging | null = null;

function ensureApp(): Messaging | null {
  if (!isPushConfigured()) return null;
  if (!app) app = initializeApp(firebaseConfig as Required<typeof firebaseConfig>);
  if (!messaging) messaging = getMessaging(app);
  return messaging;
}

/**
 * Asks the browser for notification permission, registers the service
 * worker (config passed via its own registration URL's query string — a
 * static file outside the Vite bundle can't read import.meta.env), and
 * returns the FCM registration token to save server-side. Returns null on
 * any failure (permission denied, unsupported browser, missing config) —
 * callers treat push as an optional enhancement, never a hard requirement.
 */
export async function requestWebPushToken(): Promise<string | null> {
  const msg = ensureApp();
  if (!msg || typeof window === 'undefined' || !('serviceWorker' in navigator)) return null;

  try {
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') return null;

    const swUrl = `/firebase-messaging-sw.js?apiKey=${encodeURIComponent(firebaseConfig.apiKey!)}&authDomain=${encodeURIComponent(firebaseConfig.authDomain)}&projectId=${encodeURIComponent(firebaseConfig.projectId!)}&messagingSenderId=${encodeURIComponent(firebaseConfig.messagingSenderId!)}&appId=${encodeURIComponent(firebaseConfig.appId!)}`;
    const registration = await navigator.serviceWorker.register(swUrl);

    return await getToken(msg, { vapidKey: VAPID_KEY, serviceWorkerRegistration: registration });
  } catch {
    return null;
  }
}

/** Foreground push (tab is open/focused) — background messages are handled by the service worker itself. */
export function onForegroundPush(callback: () => void): () => void {
  const msg = ensureApp();
  if (!msg) return () => undefined;
  return onMessage(msg, () => callback());
}
